import { extractPlateCandidates } from '@/utils/licensePlate'
import { preprocessPlateCapture, preprocessPlateVariants } from '@/utils/plateImagePreprocess'
import { extractRegistration } from '@/utils/registrationExtractor'

const ACCEPT_CONFIDENCE = 40
const MAX_VARIANTS = 3

let paddlePromise = null
let tesseractPromise = null
let paddleDisabled = false

function isMobileSafari() {
  if (typeof navigator === 'undefined') return false
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

async function getPaddleOcr() {
  if (paddleDisabled) throw new Error('PaddleOCR disabled')
  if (!paddlePromise) {
    paddlePromise = (async () => {
      const { PaddleOCR } = await import('@paddleocr/paddleocr-js')
      const mobile = isMobileSafari()
      return PaddleOCR.create({
        lang: 'en',
        ocrVersion: 'PP-OCRv5',
        worker: true,
        ortOptions: {
          backend: 'wasm',
          wasmPaths: 'https://cdn.jsdelivr.net/npm/onnxruntime-web/dist/',
          numThreads: 1,
          // SIMD can fail silently on some mobile browsers
          simd: !mobile,
        },
      })
    })().catch((err) => {
      paddlePromise = null
      paddleDisabled = true
      throw err
    })
  }
  return paddlePromise
}

async function runPaddleOnCanvas(canvas) {
  const ocr = await getPaddleOcr()
  const [result] = await ocr.predict(canvas)
  const items = (result?.items || []).map((item) => ({
    text: String(item.text || '').trim(),
    score: Number(item.score ?? 0),
  }))
  const rawText = items.map((i) => i.text).filter(Boolean).join('\n')
  return { engine: 'paddleocr', items, rawText }
}

async function getTesseractWorker() {
  if (!tesseractPromise) {
    tesseractPromise = (async () => {
      const { createWorker, PSM } = await import('tesseract.js')
      const worker = await createWorker('eng', 1, { logger: () => {} })
      await worker.setParameters({
        tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789- ',
        user_defined_dpi: '300',
        tessedit_pageseg_mode: PSM.SINGLE_LINE,
      })
      return { worker, PSM }
    })()
  }
  return tesseractPromise
}

async function runTesseractOnCanvas(canvas, psm) {
  const { worker, PSM } = await getTesseractWorker()
  await worker.setParameters({ tessedit_pageseg_mode: psm ?? PSM.SINGLE_LINE })
  const { data } = await worker.recognize(canvas)
  const rawText = String(data?.text || '').trim()
  const conf = Number(data?.confidence ?? 0) / 100
  const items = rawText
    ? rawText.split(/\n+/).map((text) => ({ text: text.trim(), score: conf || 0.65 }))
    : []
  return { engine: 'tesseract', items, rawText }
}

async function runOcrOnCanvas(canvas, { deep = false } = {}) {
  const results = []

  if (!paddleDisabled) {
    try {
      results.push(await runPaddleOnCanvas(canvas))
    } catch (err) {
      console.warn('PaddleOCR failed, falling back to Tesseract:', err)
      paddleDisabled = true
      paddlePromise = null
    }
  }

  try {
    results.push(await runTesseractOnCanvas(canvas))
  } catch (err) {
    console.warn('Tesseract pass failed:', err)
  }

  if (deep) {
    try {
      const { PSM } = await getTesseractWorker()
      results.push(await runTesseractOnCanvas(canvas, PSM.SINGLE_BLOCK))
    } catch (err) {
      console.warn('Tesseract block pass failed:', err)
    }
  }

  const mergedItems = []
  const rawParts = []
  for (const r of results) {
    mergedItems.push(...(r.items || []))
    if (r.rawText) rawParts.push(r.rawText)
  }

  const engine = results.some((r) => r.engine === 'paddleocr') ? 'paddleocr' : 'tesseract'
  return {
    engine,
    items: mergedItems,
    rawText: rawParts.join('\n'),
  }
}

function pickBestExtraction(extractions) {
  let best = null
  for (const ext of extractions) {
    if (!ext) continue
    const score = ext.registration ? ext.confidence : 0
    const bestScore = best?.registration ? best.confidence : 0
    if (score > bestScore) best = ext
    else if (!best?.registration && (ext.candidates?.length || 0) > (best?.candidates?.length || 0)) {
      best = ext
    }
  }
  return best
}

function fallbackFromRawText(rawText) {
  const fromLegacy = extractPlateCandidates(rawText)
  if (!fromLegacy.length) return null
  return {
    registration: fromLegacy[0],
    confidence: 35,
    candidates: fromLegacy.slice(0, 5).map((registration) => ({ registration, confidence: 35 })),
    rawText,
  }
}

async function ocrPass(sourceCanvas, options) {
  const prepared = preprocessPlateCapture(sourceCanvas, options)
  const ocr = await runOcrOnCanvas(prepared, { deep: true })
  const extracted = extractRegistration(ocr.items, { minConfidence: 28 })
  if (extracted.registration) return { ...extracted, engine: ocr.engine, ocrRaw: ocr.rawText }

  const fallback = fallbackFromRawText(ocr.rawText || extracted.rawText)
  if (fallback) return { ...fallback, engine: ocr.engine, ocrRaw: ocr.rawText }

  return { ...extracted, engine: ocr.engine, ocrRaw: ocr.rawText }
}

function collectAllCandidates(extractions) {
  const seen = new Set()
  const ordered = []
  for (const ext of extractions) {
    if (ext.registration) {
      const r = ext.registration
      if (!seen.has(r)) {
        seen.add(r)
        ordered.push(r)
      }
    }
    for (const c of ext.candidates || []) {
      const r = typeof c === 'string' ? c : c.registration
      if (r && !seen.has(r)) {
        seen.add(r)
        ordered.push(r)
      }
    }
    const fallback = fallbackFromRawText(ext.ocrRaw || ext.rawText || '')
    for (const r of fallback?.candidates?.map((x) => x.registration) || []) {
      if (r && !seen.has(r)) {
        seen.add(r)
        ordered.push(r)
      }
    }
  }
  return ordered
}

/**
 * Capture → multiple preprocess variants → OCR → extract registration.
 */
export async function recognizePlateFromCapture(sourceCanvas) {
  const variants = preprocessPlateVariants(sourceCanvas).slice(0, MAX_VARIANTS)
  const extractions = []

  for (let i = 0; i < variants.length; i += 1) {
    const variant = variants[i]
    const ocr = await runOcrOnCanvas(variant.canvas, { deep: i === 0 })
    let extracted = extractRegistration(ocr.items, { minConfidence: 28 })
    if (!extracted.registration) {
      const fallback = fallbackFromRawText(ocr.rawText || extracted.rawText)
      if (fallback) extracted = fallback
    }
    extractions.push({ ...extracted, engine: ocr.engine, ocrRaw: ocr.rawText, variant: variant.id })
    if (extracted.registration && extracted.confidence >= 55) break
  }

  const best = pickBestExtraction(extractions)
  const topCandidates = collectAllCandidates(extractions)

  const plate =
    best?.registration ||
    topCandidates[0] ||
    fallbackFromRawText(extractions.map((e) => e.ocrRaw || e.rawText).join('\n'))?.registration ||
    null

  if (import.meta.env.DEV) {
    console.info('[Plate OCR]', {
      plate,
      confidence: best?.confidence,
      engine: best?.engine,
      variant: best?.variant,
      candidates: topCandidates,
      raw: extractions.map((e) => e.ocrRaw || e.rawText).filter(Boolean).join(' | '),
    })
  }

  return {
    plate,
    confidence: best?.confidence ?? (plate ? 35 : 0),
    candidates: topCandidates,
    rawText: extractions.map((e) => e.ocrRaw || e.rawText).filter(Boolean).join(' | '),
    engine: best?.engine,
    needsConfirmation: Boolean(plate && (best?.confidence ?? 0) < ACCEPT_CONFIDENCE),
  }
}

/** Lazy-load OCR engines when plate mode opens. */
export function preloadPlateOcr() {
  void ensurePlateOcrReady().catch((e) => console.warn('Plate OCR preload failed:', e))
}

/** Wait until Paddle (or Tesseract fallback) is initialised. */
export async function ensurePlateOcrReady() {
  if (!paddleDisabled) {
    try {
      await getPaddleOcr()
      return { engine: 'paddleocr', ready: true }
    } catch (err) {
      console.warn('PaddleOCR unavailable, using Tesseract:', err)
    }
  }
  await getTesseractWorker()
  return { engine: 'tesseract', ready: true }
}